import Link from 'next/link';
import { Translate } from 'next-translate';
import { 
  ApiFilled,
  BankOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
  UserSwitchOutlined
} from '@ant-design/icons';

import { combineClassNames } from '@arkestro/arkestro-design-system';
import { truncateText } from '@utils/helpers';

import { User } from './types';
import style from './UserDropdown.module.scss';
// TODO: we need to adjust ADS to resolve the import issue `Cannot use namespace 'MenuProps' as a type.`
import type { MenuProps } from 'antd';

const vendorOrgId = (supplier: User) => {
  if (!supplier || !supplier.vendor_profile) return undefined;

  return supplier.vendor_profile.vendor_organization_id;
};

interface StringMap {
  [key: string]: string;
}

interface Props {
  enableNewSettingsPage: boolean;
  favoriteUsers: StringMap;
  t: Translate;
  user: User;
}

export const getDropdownOptions = ({
  enableNewSettingsPage = false,
  favoriteUsers = {},
  t,
  user,
}: Props) => {
  const items: MenuProps['items'] = [];

  // When enableNewSettingsPage is on, Settings, Personal Access Tokens, and My Organization
  // move to new Settings page, so hiding here.
  // TODO: Once we get the ability to hide options within ADS dropdown component, this section will be rewritten
  if (!enableNewSettingsPage && !!user.id && !user.is_deeplink && !user.is_super_admin) {
    items.push({
      icon: <SettingOutlined className={style.settingsIcon} />,
      key: 'user-settings',
      label: <Link className={style.menuItem} href='/settings/profile'>{t('general.settings')}</Link>,
    });
  }

  if (!enableNewSettingsPage && user.is_buyer && (user.role === 'owner' || user.role === 'admin')) {
    items.push({
      icon: <ApiFilled className={style.settingsIcon} />,
      key: 'personal-access-tokens',
      label: <Link className={style.menuItem} href='/personal_access_tokens' legacyBehavior>{t('personal_access_tokens.title')}</Link>,
    });
  }

  if (!enableNewSettingsPage && user.is_supplier && vendorOrgId(user) && !user.is_deeplink) {
    items.push({
      icon: <UserOutlined />,
      key: 'supplier-my-org',
      label: <Link className={style.menuItem} href={`/supplier_organizations/${vendorOrgId(user)}`}>{t('general.my_organization')}</Link>,
    });
  }

  if (!enableNewSettingsPage && user.is_buyer && user.is_admin && !user.is_deeplink) {
    items.push({
      icon: <BankOutlined className={style.settingsIcon} />,
      key: 'buyer-my-org',
      label: <Link className={style.menuItem} href='/organization'>{t('general.my_organization')}</Link>,
    });
  }

  items.push({
    icon: <LogoutOutlined className={style.settingsIcon} />,
    key: 'signout',
    label: <span className={style.menuItem}>{t('general.sign_out')}</span>,
  });

  if (user.impersonating) {
    items.push({
      icon: <LogoutOutlined className={combineClassNames(style.settingsIcon, 'icon-red')} />,
      key: 'stop-impersonating',
      label: <span className={style.menuItem}>{t('general.stop_impersonating')}</span>,
    });
  }

  if (user.impersonating || user.is_super_admin) {
    items.push({ type: 'divider' });
    items.push(...Object.entries(favoriteUsers || {}).map(([id, email]) => (
      {
        icon: (<UserSwitchOutlined className={combineClassNames(style.settingsIcon, 'icon-red')} />),
        key: `impersonate-user-${id}`,
        label: (
          <span className={style.menuItem} data-testid='switch-impersonation-button' title={`Impersonate ${email}`}>
            {truncateText(email, 25)}
          </span>
        ),
      }
    )));
  }

  return items;
};
